"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.invalidOtpCodeSentMessage = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const zod_1 = require("zod");
const strings_1 = require("../../../../constants/strings");
const query_keys_1 = require("../../../../queries/query-keys");
const sessionQuery_1 = require("../../../../queries/sessionQuery");
const sessionRepository_1 = require("../../../../repositories/sessionRepository");
const userRepository_1 = __importDefault(require("../../../../repositories/userRepository"));
const Root_1 = require("../../../../router/Root");
const userSelectors_1 = require("../../../../selectors/userSelectors");
const zustandStore_1 = require("../../../../zustandStore");
const formWrapper_1 = __importDefault(require("../hsbc/formWrapper"));
const useValidation_1 = require("../hsbc/hooks/useValidation");
const formInput_1 = __importDefault(require("../hsbc/steps/common/formInput"));
const logger = (0, pino_1.default)();
const lexico = new lexico_1.default();
const twoFactorSchema = zod_1.z.object({
    authCode: zod_1.z.string().min(6, 'Authentication code must be at least 6 characters long'),
});
exports.invalidOtpCodeSentMessage = 'Incorrect code. Please try again.';
const SetupTwoFactorAuth = ({ showNotificationMessage = false }) => {
    const navigate = (0, react_router_1.useNavigate)();
    const theme = (0, material_1.useTheme)();
    const logout = (0, sessionQuery_1.useSessionLogoutQuery)();
    const [showSecretKey, setShowSecretKey] = (0, react_1.useState)(false);
    const { setPageLoading, registration, setNotification, setMfaData, setHasUserSkipMfa } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const { mfaData, showTwoFactorAuth } = registration;
    const [formState, setFormState] = (0, react_1.useState)({
        authCode: '',
    });
    const { validateField, clearFieldError, fieldErrors, validateAllSchema } = (0, useValidation_1.useValidation)();
    const [isFormPending, setIsFormPending] = (0, react_1.useState)(false);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => {
            const updatedState = { ...prev, [name]: value };
            clearFieldError(name);
            return updatedState;
        });
    };
    const handleBlur = (e) => {
        if (e) {
            const { name = '' } = e.target;
            validateField(twoFactorSchema, name, formState);
        }
    };
    const onActivateUser = async () => {
        try {
            const user = await userRepository_1.default.getUser();
            Root_1.queryClient.setQueryData([query_keys_1.userKeys.getUser], user);
            const isTsAdmin = (0, userSelectors_1.isSupportAdmin)(user?.roles);
            if (user && !isTsAdmin && !(0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles) && user?.roles.length === 0) {
                logout.mutate();
                setNotification([
                    {
                        type: strings_1.notificationStrings.ERROR,
                        message: lexico.trc('Error message', 'Your account has been disabled, please contact your company or team administrator.'),
                    },
                ]);
            }
            else if (isTsAdmin) {
                void navigate('/settings/overview');
            }
            else {
                const path = (0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles || []) ? '/wallet' : '/wallet/my-cards';
                void navigate(path);
            }
        }
        catch (error) {
            logger.error('error in onActivateUser:', error);
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: 'An error occurred while activating the user. Please try again.',
                },
            ]);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsFormPending(true);
        validateAllSchema(twoFactorSchema, formState);
        if (!twoFactorSchema.safeParse(formState).success) {
            return;
        }
        try {
            const data = await sessionRepository_1.SessionRepository.sendMfaCode({
                code: formState.authCode,
                challengeName: 'SOFTWARE_TOKEN_MFA',
            });
            setMfaData(data);
            if (data.mfaOtp.status === 'authenticated') {
                setFormState({ authCode: '' });
            }
            globalThis.localStorage.setItem('hasUserSkipMfa', 'false');
            return onActivateUser();
        }
        catch (error) {
            logger.error({ error });
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: exports.invalidOtpCodeSentMessage,
                },
            ]);
        }
        finally {
            setIsFormPending(false);
            setPageLoading(false);
        }
    };
    const handleSkip = async () => {
        globalThis.localStorage.removeItem('hasUserSkipMfa');
        setHasUserSkipMfa(true);
        globalThis.localStorage.setItem('hasUserSkipMfa', 'true');
        try {
            await sessionRepository_1.SessionRepository.sendMfaCodeChallengeSkip();
            return onActivateUser();
        }
        catch {
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: 'An error occurred while skipping MFA. Please try again.',
                },
            ]);
        }
    };
    return showTwoFactorAuth ? ((0, jsx_runtime_1.jsxs)(formWrapper_1.default, { style: { width: 'auto', border: 'none', paddingTop: 0 }, formName: "", onSubmit: handleSubmit, submitButton: {
            label: strings_1.sessionStrings.CONTINUE,
            isDisabled: !twoFactorSchema.safeParse(formState).success,
        }, isFormPending: isFormPending, children: [globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP && showNotificationMessage ? ((0, jsx_runtime_1.jsx)(material_1.Alert, { severity: "warning", variant: "filled", style: { marginBottom: theme?.spacing(2) }, children: "Starting 30th of March, 2025, Go will require 2FA when accessing your account" })) : null, (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h5", gutterBottom: true, children: "Enhance Your Security with 2FA" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body1", gutterBottom: true, children: "Protect your account with Two-Factor Authentication (2FA) for an extra layer of security. Enabling 2FA significantly reduces unauthorized access, offering you peace of mind." }), (0, jsx_runtime_1.jsx)(material_1.Divider, { style: { margin: '20px 0' } }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h5", gutterBottom: true, children: "Getting Started:" }), (0, jsx_runtime_1.jsxs)(material_1.Typography, { variant: "body1", gutterBottom: true, children: [(0, jsx_runtime_1.jsx)("strong", { children: "1. Install an Authenticator App:" }), " (", (0, jsx_runtime_1.jsxs)(material_1.Typography, { variant: "caption", children: ["If you already have an app installed, you can proceed to ", (0, jsx_runtime_1.jsx)("strong", { children: "Step 2" }), "."] }), ") Choose and download a trusted app like Google Authenticator, Authy, or Microsoft Authenticator on your mobile device."] }), (0, jsx_runtime_1.jsxs)(material_1.Typography, { variant: "body1", gutterBottom: true, children: [(0, jsx_runtime_1.jsx)("strong", { children: "2. Set Up Your Account:" }), " Open the app and follow the prompts to add an account. Scan the QR code provided to complete the setup."] }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body1", gutterBottom: true, children: "Ensure your account\u2019s security with just a few easy steps!" }), (0, jsx_runtime_1.jsx)("div", { style: { textAlign: 'center', margin: '20px 0' }, children: (0, jsx_runtime_1.jsx)("img", { src: mfaData?.mfaOtp?.qrCode || '', alt: "QR Code" }) }), (0, jsx_runtime_1.jsx)(material_1.Link, { href: "#", onClick: e => {
                    e.preventDefault();
                    setShowSecretKey(!showSecretKey);
                }, style: {
                    fontWeight: '600',
                    fontSize: '12px',
                    lineHeight: '20px',
                    alignItems: 'center',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    color: 'rgb(0, 174, 255)',
                    display: 'block',
                    margin: 'auto',
                    width: '100%',
                    textDecoration: 'none',
                }, children: showSecretKey ? 'HIDE SECRET KEY' : 'SHOW SECRET KEY' }), showSecretKey && ((0, jsx_runtime_1.jsxs)(material_1.Typography, { variant: "body1", gutterBottom: true, style: { wordBreak: 'break-word' }, children: ["Secret Key: ", mfaData?.mfaOtp?.otpSecret || 'Not Available'] })), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                    ? strings_1.sessionStrings.ENTER_YOUR_AUTH_APP_CODE_HSBC
                    : strings_1.sessionStrings.ENTER_YOUR_AUTH_APP_CODE_AMEX, name: "authCode", value: formState.authCode, error: fieldErrors['authCode'], onChange: handleInputChange, onBlur: handleBlur }), mfaData?.skipMfaAllowed && !registration.hasUserSkipMfa ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(material_1.Link, { href: "#", onClick: e => {
                            e.preventDefault();
                            void handleSkip();
                        }, style: {
                            fontWeight: 'bold',
                            fontSize: '14px',
                            lineHeight: '24px',
                            color: theme.palette.tsGoColor.red,
                            display: 'block',
                            padding: '10px',
                            textAlign: 'center',
                            textDecorationColor: theme.palette.tsGoColor.red,
                        }, children: "Skip 2FA setup for now" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "subtitle2", gutterBottom: true, style: { wordBreak: 'break-word' }, children: "2FA will become mandatory to access Go starting on 30th of March, 2025" })] })) : null] })) : null;
};
exports.default = SetupTwoFactorAuth;
